// Suite onboarding commit replay (ADR 0032). On Finish the client re-applies the
// STAGED wizard answers into the now-empty modules (after wipe-if-demo). Every
// write is idempotent / resume-safe — list-then-create, or tolerate a duplicate —
// so a crashed replay re-run by any admin never duplicates. The per-section
// applied markers (user-api commit/applied/:key) skip whole sections on resume;
// these guards cover a mid-section crash before the marker landed.
//
// The wizard sections stage the SAME payload shapes these functions read, so the
// staged blob is the single contract between staging and replay.
//
// SEAM (issue 195, ADR 0068): these writes are not ten user actions — they are the steps of
// ONE, the Finish press, and they are issued from inside the single `useApiMutation` in
// `components/onboarding/WizardCommitScreen.vue`, which owns the pending state and renders the
// failure (`replayError`, beside the step marker that stopped). So this module is on the
// mutation-seam guard's `SEAM_COVERED_FILES` map against that screen, and every call below
// carries the `seam-covered:` marker naming it. Add a write here only if it is another step of
// that same press; anything a user triggers on its own belongs in the seam at its own call site.
import { apprenticeApi, atsApi, peopleApi, timeApi } from './api';
import { companyHolidayKey, normalizeCompanyHolidays, type StagedCompanyHoliday } from './companyHolidays';

// Order matters: company name → general (company profile + org units) → people
// vocabularies → ATS settings → booking types + company holidays → apprenticeship. company name runs
// always (both paths); the rest run only when their section was staged. Every
// staged key MUST appear here — user-api's finish requires an applied marker for
// each, so a staged key missing from the order would deadlock the commit. Keep in
// sync with the wizard's section keys (first_people was removed in issue 214 — invites
// are done in the admin page after setup, not in the wizard).
export const REPLAY_ORDER = ['general', 'people', 'ats', 'time_tracking', 'apprenticeship'] as const;

function slug(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'x'
  );
}

function normalizeList(v: any): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
}

function isConflict(e: any): boolean {
  return e?.response?.status === 409;
}

// Company name is auto-applied on BOTH paths (even an all-skipped "start empty"),
// from the checkout-captured master tenants.name. People seeds an empty profile at
// bootstrap; the PUT is a merge, so re-running on resume is a no-op and it never
// clobbers the general section's company-profile basics.
export async function replayCompanyName(companyName: string): Promise<void> {
  const name = (companyName || '').trim();
  if (!name) return; // tenants.name is required at checkout, but never PUT an empty name (400)
  // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'company name'; one press, one seam call, one rendered outcome
  await peopleApi.put('/tenant/company-profile', { companyName: name });
}

// general: company-profile basics (People-owned) + the org structure (4 unit types).
async function replayGeneral(payload: any): Promise<void> {
  const profile: Record<string, any> = {};
  // Company name is required by people-api's company-profile write; the wizard's
  // general section stages it (required field), so always send it here.
  const name = String(payload?.companyName || '').trim();
  if (name) profile.companyName = name;
  // hqCountryCode is the picker's ISO 3166-1 alpha-2 code; hqCountry is the same
  // fact as the localized display name, which is what ats-api's company-profile
  // projection and the job boards receive (A21, issue 57). Both are staged
  // together by the general section, and both are plain strings here.
  for (const k of [
    'legalForm',
    'industry',
    'employeeCountRange',
    'websiteUrl',
    'hqStreet',
    'hqPostalCode',
    'hqCity',
    'hqCountry',
    'hqCountryCode',
  ]) {
    const v = payload?.[k];
    if (typeof v === 'string' && v.trim()) profile[k] = v.trim();
  }
  if (payload?.foundedYear !== undefined && String(payload.foundedYear).trim() !== '') {
    const year = Number(payload.foundedYear);
    if (Number.isFinite(year)) profile.foundedYear = year;
  }
  // The company-profile PUT is a merge (people-api writes only provided keys), so
  // this never clobbers the name replayCompanyName applied.
  // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'general' (company profile), same press
  if (Object.keys(profile).length) await peopleApi.put('/tenant/company-profile', profile);

  const orgUnits = Array.isArray(payload?.orgUnits) ? payload.orgUnits : [];
  if (!orgUnits.length) return;
  const existing = await peopleApi
    .get('/org-units')
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch(() => [] as any[]);
  const seen = new Set(existing.map((u: any) => `${u.type}:${String(u.name || '').toLowerCase()}`));
  const VALID_TYPES = new Set(['department', 'location', 'team', 'cost_center']);
  for (const u of orgUnits) {
    const type = VALID_TYPES.has(u?.type) ? u.type : 'department';
    const name = String(u?.name || '').trim();
    if (!name || seen.has(`${type}:${name.toLowerCase()}`)) continue;
    try {
      // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'general' (org units), 409-tolerant, same press
      await peopleApi.post('/org-units', { type, name });
    } catch (e) {
      if (!isConflict(e)) throw e;
    }
    seen.add(`${type}:${name.toLowerCase()}`);
  }
}

// people: the salutation vocabulary + the three recruiting-option lists.
async function replayPeople(payload: any): Promise<void> {
  await replayVocabulary('/salutations', normalizeList(payload?.salutations));
  await replayVocabulary('/recruiting-options/contract-type', normalizeList(payload?.contractTypes));
  await replayVocabulary('/recruiting-options/work-time-model', normalizeList(payload?.workTimeModels));
  await replayVocabulary('/recruiting-options/work-location-type', normalizeList(payload?.workLocationTypes));
}

// Shared key/label vocabulary replay (salutations + recruiting options share the
// { key, label } contract and a `key` de-dupe list). Idempotent by slug(label).
async function replayVocabulary(path: string, labels: string[]): Promise<void> {
  if (!labels.length) return;
  const existing = await peopleApi
    .get(path)
    .then((r) => (Array.isArray(r.data) ? r.data.map((o: any) => String(o.key)) : []))
    .catch(() => [] as string[]);
  const seen = new Set(existing);
  for (const label of labels) {
    const key = slug(label);
    if (seen.has(key)) continue;
    try {
      // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'people' (vocabularies), 409-tolerant, same press
      await peopleApi.post(path, { key, label });
    } catch (e) {
      if (!isConflict(e)) throw e;
    }
    seen.add(key);
  }
}

/** What replaying the hiring chain (issue 23) ended up doing. */
type WorkflowBuilderReplayOutcome = {
  /** A genuine failure (network/server error) — non-blocking: the tenant keeps
   * the lean default it was seeded with, onboarding still completes, and the
   * commit screen names it with the retry route (the builder in ATS workflow
   * settings). There is no retry loop here. */
  workflowFailed?: boolean;
  /**
   * #380: `activate: 'ifStaffed'` never refuses — a generated definition whose
   * own council stages are all staffed is published, the rest are saved as
   * drafts. This is NOT a failure (nothing above threw, onboarding completes
   * exactly like the activated case) — it is the deduped role keys a drafted
   * definition named, for the commit screen to name in turn.
   */
  workflowDraftedRoleKeys?: string[];
};

// The hiring chain (issue 23): the wizard STAGES the generator's four answers, and
// this is the one place they are turned into workflow definitions — never during the
// wizard, where the modules must stay untouched (and, on the demo path, are wiped
// right before this runs).
//
// Generation is idempotent on the answer hash, per entity type, so a commit resumed
// after a partial success re-posts safely: what already exists comes back unchanged.
async function replayWorkflowBuilder(staged: any): Promise<WorkflowBuilderReplayOutcome> {
  if (!staged || typeof staged !== 'object') return {};
  const answers = {
    worksCouncil: staged.worksCouncil === true,
    sbv: staged.sbv === true,
    legalSignOff: staged.legalSignOff === true,
    interviewShape: staged.interviewShape === 'assessment_centre' ? 'assessment_centre' : 'interviews_only',
  };
  // All councils off + plain interviews is the lean default the tenant already has,
  // so those answers generate nothing at all: skip the round trip entirely.
  const isLeanDefault =
    !answers.worksCouncil &&
    !answers.sbv &&
    !answers.legalSignOff &&
    answers.interviewShape === 'interviews_only';
  if (isLeanDefault) return {};
  try {
    // `'ifStaffed'` (#380), not `true`: onboarding must never fail the commit over
    // a council role nobody has been assigned yet — it publishes what it can and
    // drafts the rest, per definition, rather than refusing the whole run the way
    // `true` would. `false` is not right either: it would draft even a fully
    // staffed chain, which is not what a fresh tenant's Finish press means.
    // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'ats' (workflow builder), same press
    const res = await atsApi.post('/tenant/workflows/builder', { answers, activate: 'ifStaffed' });
    const created = Array.isArray(res?.data?.created) ? res.data.created : [];
    const draftedRoleKeys = Array.from(
      new Set(
        created
          .filter((item: any) => item?.status === 'draft')
          .flatMap((item: any) => (Array.isArray(item.unstaffedRoleKeys) ? item.unstaffedRoleKeys : [])),
      ),
    ) as string[];
    return draftedRoleKeys.length ? { workflowDraftedRoleKeys: draftedRoleKeys } : {};
  } catch {
    return { workflowFailed: true };
  }
}

async function replayAts(
  payload: any,
  logoFile: File | null,
): Promise<{ logoMissing?: boolean; workflowFailed?: boolean; workflowDraftedRoleKeys?: string[] }> {
  const result: { logoMissing?: boolean; workflowFailed?: boolean; workflowDraftedRoleKeys?: string[] } = {};
  const email: any = {};
  if (payload?.fromName !== undefined) email.smtpFromName = payload.fromName || '';
  if (payload?.signature !== undefined) email.defaultSignature = payload.signature || '';
  // NO `mode` here: from-name + signature are display fields that apply under the
  // system-default mode. Sending `mode:'tenant_override'` makes ats-api reject the
  // PUT (400) unless full SMTP+IMAP credentials exist — which the wizard never
  // collects — deadlocking the commit. Letting the server resolve the mode from
  // existing settings persists these fields exactly like the pre-0032 wizard did.
  // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'ats' (email settings), same press
  if (Object.keys(email).length) await atsApi.put('/tenant/email-settings', email);
  // Job posting settings (ADR 0032): candidate contact + job-ad logo, ATS-owned.
  const jps: any = {};
  if (payload?.contactName !== undefined) jps.contactName = payload.contactName || null;
  if (payload?.contactEmail !== undefined) jps.contactEmail = payload.contactEmail || null;
  // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'ats' (job posting settings), same press
  if (Object.keys(jps).length) await atsApi.put('/tenant/job-posting-settings', jps);
  // The logo File is held client-side only (ADR 0032). If one was uploaded, apply it.
  if (logoFile) {
    const fd = new FormData();
    fd.append('logo', logoFile);
    // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'ats' (job-ad logo), same press
    await atsApi.post('/tenant/job-posting-settings/logo', fd);
  } else if (payload?.logoPicked) {
    // The wizard recorded a logo was picked, but the File is gone (reload, or a second
    // admin resumed the commit from another browser). Report it so the commit screen
    // warns the admin instead of silently finishing without the logo (it's optional —
    // Raven's readiness guard enforces it later — so this never blocks the commit).
    result.logoMissing = true;
  }
  // Last, so a failed (or drafted) chain cannot cost the settings writes above.
  const workflowOutcome = await replayWorkflowBuilder(payload?.workflow);
  if (workflowOutcome.workflowFailed) result.workflowFailed = true;
  if (workflowOutcome.workflowDraftedRoleKeys?.length) {
    result.workflowDraftedRoleKeys = workflowOutcome.workflowDraftedRoleKeys;
  }
  return result;
}

async function replayTime(payload: any): Promise<void> {
  await replayBookingTypes(normalizeList(payload?.bookingTypes));
  await replayCompanyHolidays(normalizeCompanyHolidays(payload?.companyHolidays));
}

async function replayBookingTypes(names: string[]): Promise<void> {
  if (!names.length) return;
  const existing = await timeApi
    .get('/booking-types')
    .then((r) => (Array.isArray(r.data) ? r.data.map((b: any) => String(b.name || '').toLowerCase()) : []))
    .catch(() => [] as string[]);
  const seen = new Set(existing);
  for (const name of names) {
    if (seen.has(name.toLowerCase())) continue;
    try {
      // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'time_tracking' (booking types), 409-tolerant, same press
      await timeApi.post('/booking-types', { name });
    } catch (e) {
      if (!isConflict(e)) throw e;
    }
    seen.add(name.toLowerCase());
  }
}

// The days the company itself closes (Betriebsferien), staged in the wizard's
// Timecard section. Idempotent by name + first day: an entry a crashed replay
// already wrote is skipped on resume, and time-api has no unique key to answer
// 409 with, so the list read IS the duplicate guard here.
async function replayCompanyHolidays(entries: StagedCompanyHoliday[]): Promise<void> {
  if (!entries.length) return;
  const existing = await timeApi
    .get('/company-holidays')
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch(() => [] as any[]);
  const seen = new Set(
    existing.map((h: any) => companyHolidayKey({ name: String(h?.name || ''), startDate: String(h?.startDate || '') })),
  );
  for (const entry of entries) {
    const key = companyHolidayKey(entry);
    if (seen.has(key)) continue;
    // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'time_tracking' (company holidays), list-guarded, same press
    await timeApi.post('/company-holidays', entry);
    seen.add(key);
  }
}

// apprenticeship (issue 214, recut per ADR 0053): the Ausbildungsberufe live on
// the module's settings singleton now — the pre-recut `/programs` endpoints are
// gone. The Berichtsheft cadence is no longer offered (issue 819: there is no
// daily form), so a `reportBookType` staged by a wizard from before that is
// ignored. Idempotent: professions merge by slug(title) into whatever the
// settings already hold.
async function replayApprentice(payload: any): Promise<void> {
  const titles = normalizeList(payload?.professions);
  if (!titles.length) return;

  const existing = await apprenticeApi
    .get('/settings')
    .then((r) => (Array.isArray(r.data?.professions) ? (r.data.professions as any[]) : []))
    .catch(() => [] as any[]);
  const byKey = new Map<string, { key: string; title: string }>();
  for (const p of existing) {
    const key = String(p?.key || '');
    const title = String(p?.title || '');
    if (key && title) byKey.set(key, { key, title });
  }
  for (const title of titles) {
    const key = slug(title);
    if (!byKey.has(key)) byKey.set(key, { key, title });
  }
  const body = { professions: [...byKey.values()] };
  // seam-covered: workspace-ui/src/components/onboarding/WizardCommitScreen.vue — Finish replay step 'apprenticeship' (settings), same press
  await apprenticeApi.put('/settings', body);
}

// Replay ONE staged section. Returns the non-blocking outcomes the commit screen has
// to name — a picked logo lost before it could be uploaded, a hiring chain that could
// not be generated at all, and a hiring chain generated but with a definition saved
// as a draft because a council role it names is unstaffed (all three ats only).
// Everything else resolves cleanly or throws for the commit screen.
export async function replayStep(
  key: string,
  payload: any,
  logoFile: File | null,
): Promise<{ logoMissing?: boolean; workflowFailed?: boolean; workflowDraftedRoleKeys?: string[] }> {
  switch (key) {
    case 'general':
      await replayGeneral(payload);
      return {};
    case 'people':
      await replayPeople(payload);
      return {};
    case 'ats':
      return await replayAts(payload, logoFile);
    case 'time_tracking':
      await replayTime(payload);
      return {};
    case 'apprenticeship':
      await replayApprentice(payload);
      return {};
    default:
      return {};
  }
}
